import { z } from 'zod';
import { BEELDFORMATEN } from './constanten.js';

/**
 * API-contracten die de webapp en de server delen.
 *
 * Door ze hier te definiëren en met zod te valideren, kan een wijziging in de
 * server niet stilzwijgend de client breken: de typen komen uit één bron.
 */

export const taakStatusSchema = z.enum(['wacht', 'bezig', 'klaar', 'mislukt', 'geannuleerd']);
export const taakSoortSchema = z.enum([
  'inspectie',
  'proxy',
  'export',
  'ai-generatie',
  'transcriptie',
]);

export const taakSchema = z.object({
  id: z.string(),
  soort: taakSoortSchema,
  status: taakStatusSchema,
  projectId: z.string().nullable(),
  assetId: z.string().nullable(),
  voortgang: z.number(),
  fase: z.string().nullable(),
  pogingen: z.number(),
  foutCode: z.string().nullable(),
  foutMelding: z.string().nullable(),
  resultaat: z.record(z.string(), z.unknown()).nullable(),
  gemaaktOp: z.string(),
  gestartOp: z.string().nullable(),
  afgerondOp: z.string().nullable(),
});

export const exportSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  projectId: z.string(),
  bestandsnaam: z.string(),
  grootteBytes: z.number(),
  duurSeconden: z.number().nullable(),
  breedte: z.number().nullable(),
  hoogte: z.number().nullable(),
  fps: z.number().nullable(),
  codec: z.string().nullable(),
  audioCodec: z.string().nullable(),
  methode: z.enum(['remotion', 'ffmpeg-basistrack', 'ffmpeg-basistrack+remotion']).nullable(),
  gemaaktOp: z.string(),
  downloadUrl: z.string(),
});

export const projectSamenvattingSchema = z.object({
  id: z.string(),
  naam: z.string(),
  versie: z.number(),
  breedte: z.number(),
  hoogte: z.number(),
  fps: z.number(),
  gewijzigdOp: z.string(),
  gemaaktOp: z.string(),
  aantalItems: z.number(),
  laatsteVideo: z
    .object({
      id: z.string(),
      bestandsnaam: z.string(),
      url: z.string(),
      duurSeconden: z.number().nullable(),
    })
    .nullable()
    .optional(),
});

export const capaciteitenSchema = z.object({
  project: z.object({
    fps: z.number(),
    maxSeconden: z.number(),
    maxExportHoogte: z.number(),
    proxyHoogte: z.number(),
  }),
  beeldformaten: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      breedte: z.number(),
      hoogte: z.number(),
    }),
  ),
  fonten: z.array(z.object({ id: z.string(), label: z.string(), stack: z.string() })),
  integraties: z.object({
    aiVideo: z.object({
      geconfigureerd: z.boolean(),
      provider: z.string(),
      ontbrekend: z.array(z.string()),
    }),
    transcriptie: z.object({
      geconfigureerd: z.boolean(),
      provider: z.string().nullable(),
      ontbrekend: z.array(z.string()),
    }),
  }),
  wachtrij: z.object({ media: z.number(), export: z.number(), ai: z.number() }),
  budget: z.object({ perDagUsd: z.number(), perMaandUsd: z.number() }),
  opslag: z.object({ driver: z.string() }),
  ffmpeg: z.object({ ffmpeg: z.string(), ffprobe: z.string() }),
});

export type Taak = z.infer<typeof taakSchema>;
export type ExportItem = z.infer<typeof exportSchema>;
export type ProjectSamenvatting = z.infer<typeof projectSamenvattingSchema>;
export type Capaciteiten = z.infer<typeof capaciteitenSchema>;

/** Standaardcapaciteiten voor als de server nog niet geantwoord heeft. */
export const STANDAARD_CAPACITEITEN: Capaciteiten = {
  project: { fps: 30, maxSeconden: 60, maxExportHoogte: 1080, proxyHoogte: 540 },
  beeldformaten: Object.values(BEELDFORMATEN),
  fonten: [{ id: 'Inter', label: 'Inter (standaard)', stack: "'Inter','DejaVu Sans',Arial,sans-serif" }],
  integraties: {
    aiVideo: { geconfigureerd: false, provider: 'xai', ontbrekend: ['XAI_API_KEY'] },
    transcriptie: { geconfigureerd: false, provider: null, ontbrekend: ['OPENAI_API_KEY'] },
  },
  wachtrij: { media: 2, export: 1, ai: 2 },
  budget: { perDagUsd: 10, perMaandUsd: 100 },
  opslag: { driver: 'lokaal' },
  ffmpeg: { ffmpeg: 'ffmpeg', ffprobe: 'ffprobe' },
};
